import IconArrowMinimalRight from "@shared/components/icons/ArrowMinimalRight"
import { cx } from "@shared/components/untitledui/util/cx"
import type { CardStackSummary } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import unitComparisonViewAction from "cc-frontend/lib/actions/course/calendar/UNIT_COMPARISON_VIEW"
import type ModalManager from "cc-frontend/services/modal-manager"
import moment from "moment"
import { useAsEmberDocument } from "react-app/hooks/useAsEmberDocument"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useEmberService } from "react-app/providers/ember-service-context"
import { Button } from "react-aria-components"
import styles from "./UnitsInHeader.module.css"

/** "Oct 14 - 24" within a month, "Apr 13 - Jun 29" across them. */
function dateRange(startDate: string, endDate: string): string {
  const start = moment(startDate)
  const end = moment(endDate)
  return `${start.format("MMM D")} - ${end.format(start.isSame(end, "month") ? "D" : "MMM D")}`
}

/** A course and the planbook and calendar it is planned on, as the comparison modal wants them. */
interface CourseModels {
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

interface Props {
  units: Array<CardStackSummary> | null
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  editCourseTitleAndOrder: (courseId: string) => void
  /**
   * Course models for the lesson this is on, if different from the unit's.
   */
  lessonModels?: CourseModels
  showUnitReadonly?: boolean
  /** Drops the block's own gutter, for a host that already pads its children. */
  flush?: boolean
  className?: string
  pillClassName?: string
}

/**
 * The day's units, painted above the lesson, each row opening the unit-comparison modal. The
 * Ember twin (`Planner::CardStacks::Lesson::UnitsInHeader`) keeps the pre-redesign look on its
 * own module — the two are no longer meant to match.
 */
export default tracked(function UnitsInHeader({
  units,
  course,
  planbook,
  rotationCalendar,
  dateString,
  editCourseTitleAndOrder,
  lessonModels,
  showUnitReadonly = false,
  flush = false,
  className,
  pillClassName,
}: Props) {
  const modalManager = useEmberService<ModalManager>("modal-manager")
  const asEmberDocument = useAsEmberDocument()
  const dispatch = useDispatch()

  if (!units || units.length === 0) return null

  const lessonSide = lessonModels ?? { course, planbook, rotationCalendar }

  const viewUnit = (unitId: string) => {
    modalManager.openModal("planner/modals/unit-comparison-modal", {
      unit: {
        planbook: asEmberDocument(planbook),
        course: asEmberDocument(course),
        rotationCalendar: asEmberDocument(rotationCalendar),
        unitId,
        // A followed course's unit is someone else's plan: its settings aren't edited from here.
        editCourseTitleAndOrder: lessonModels ? undefined : editCourseTitleAndOrder,
      },
      lesson: {
        planbook: asEmberDocument(lessonSide.planbook),
        course: asEmberDocument(lessonSide.course),
        rotationCalendar: asEmberDocument(lessonSide.rotationCalendar),
        dateString,
        editCourseTitleAndOrder,
      },
      showUnitReadonly,
    })
    dispatch(
      unitComparisonViewAction({
        unitId,
        course,
        planbook,
        followingCourseId: lessonModels?.course.id,
      }),
    )
  }

  return (
    <div
      className={cx(styles.unitsBlockWrapper, flush && styles.unitsBlockWrapperFlush, className)}
    >
      <div className={styles.unitsBlock} data-test="units block">
        {units.map((unitCardStack) => {
          // The calendar entry carries the date range the summary doesn't.
          const unit = course.attributes.calendar.units.find((u) => u.id === unitCardStack.id)
          return (
            <Button
              key={unitCardStack.id}
              className={cx(styles.unit, pillClassName)}
              data-test="unit row"
              onPress={() => viewUnit(unitCardStack.id)}
            >
              <div
                className={`take-course-background-color set-course-color-vars ${styles.unitColorBlock}`}
                data-color={unitCardStack.attributes.color}
              ></div>
              <div className={styles.unitText}>
                <div className={styles.unitTitle}>{unitCardStack.attributes.title}</div>
                <div className={styles.unitDateRange}>
                  {unit && (
                    <>
                      {dateRange(unit.startDate, unit.endDate)}
                      {unit.dateStrings &&
                        ` (Day ${unit.dateStrings.indexOf(dateString) + 1} of ${unit.unitLength})`}
                    </>
                  )}
                </div>
              </div>
              <div className={styles.unitChevron}>
                <IconArrowMinimalRight />
              </div>
            </Button>
          )
        })}
      </div>
    </div>
  )
})
